import React from 'react';

import { EmptyIllustration } from '@dashboard/components/empty-states/EmptyIllustration';
import Link from '@dashboard/components/mui-atoms/Link';
import Header from '@dashboard/components/pages/QueryListPage/PageWrapper/Header';
import { createTableComponent } from '@dashboard/components/pages/QueryListPage/TableList/WpDataGrid';
import {
  type FWorkflowEventFragment,
  useWorkflowEventsQuery,
  useWorkflowQuery,
} from '@dashboard/generated/wp/platform';
import usePlatformNavigator from '@dashboard/hooks/navigate/usePlatformNavigator';
import { useWorkflowsListHref } from '@dashboard/hooks/route/hrefs';
import { useCurrentPage } from '@dashboard/hooks/route/useCurrentPage';
import useQueryDataTableProps from '@dashboard/hooks/useQueryDataTableProps';
import { getWorkflowEventSourceLabel, getWorkflowEventStatusLabel } from '@dashboard/models/WorkflowEvent';
import { Chip, Paper, Skeleton, Typography } from '@mui/material';

import { DateFormatter } from '@waypoint/react/formatters';

function EmptyTableMessage() {
  const workflowsHref = useWorkflowsListHref();
  return (
    <EmptyIllustration
      caption={
        <Typography color="text.secondary">
          Your{' '}
          <Link href={workflowsHref} color="inherit" underline="always">
            workflow runs
          </Link>{' '}
          will show up here.
        </Typography>
      }
    />
  );
}

const WorkflowEventsTable = createTableComponent<FWorkflowEventFragment>({
  emptyResults: <EmptyTableMessage />,

  columns: [
    {
      headerName: 'Status',
      renderCell: ({ row }) => (
        <Chip
          variant="outlined"
          size="small"
          label={getWorkflowEventStatusLabel(row)}
          sx={{ borderRadius: 3, lineHeight: 1 }}
        />
      ),
      minWidth: 192,
      maxWidth: 192,
    },
    {
      headerName: 'Source',
      minWidth: 128,
      maxWidth: 128,
      renderCell: ({ row }) => <Typography noWrap>{getWorkflowEventSourceLabel(row)}</Typography>,
    },
    {
      headerName: 'Timestamp',
      renderCell: ({ row }) => <DateFormatter value={row.createdAt} />,
    },
    {
      headerName: 'Workflow',
      renderCell: function WorkflowCell({ row }) {
        const { data } = useWorkflowQuery({
          variables: { id: row.workflowId! },
          skip: row.workflowId === null,
        });

        const workflow = data?.Workflow;

        if (row.workflowId === null) {
          return <>none</>;
        }

        if (!workflow) {
          return <Skeleton />;
        }
        return <Typography color="text.primary">{workflow.name}</Typography>;
      },
    },
    {
      headerName: 'ID',
      renderCell: ({ row }) => <>{row.id}</>,
    },
  ],
});

export default function WorkflowRunsList() {
  const page = useCurrentPage();
  const variables = { page };
  const { data } = useWorkflowEventsQuery({ variables, fetchPolicy: 'network-only' });
  const navigator = usePlatformNavigator();
  const { pagination } = useQueryDataTableProps(data?.WorkflowEvents);
  const nodes = data?.WorkflowEvents.nodes;

  return (
    <Paper>
      <Header title="Workflow runs" pagination={pagination} />
      <WorkflowEventsTable
        nodes={nodes}
        onRowClick={({ id }) => {
          const modelIds = nodes?.map((n) => n.id) ?? [];
          navigator.showWorkflowRun(id, { modelIds });
        }}
      />
    </Paper>
  );
}
